import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select, { MultiValue, StylesConfig } from 'react-select';
import {
  getXperience,
  updateXperience,
  updateXperienceMedia,
  deleteMedia,
  uploadMedia,
  deleteXperience,
  Xperience,
  XperienceScene,
  Media,
  reorderXperienceMedia,
  createXperienceScene,
  updateXperienceScene,
  deleteXperienceScene,
  reorderXperienceScenes,
  addMediaToXperienceScene,
  reorderXperienceSceneMedia,
  removeMediaFromXperienceScene,
  emailPrivateXperienceQR,
  getXperienceTags
} from '../services/api';
import Navbar from '../components/Navbar';
import Button from '../components/Button';
import Input from '../components/Input';
import ConfirmDialog from '../components/ConfirmDialog';
import LocationPicker from '../components/LocationPicker';
import { t } from '../i18n';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './XperienceDetail.css';

const LAST_XPERIENCE_LOCATION_KEY = 'xperience_last_location';

interface TagOption {
  value: number;
  label: string;
}

const customSelectStyles: StylesConfig<TagOption, true> = {
  control: (provided) => ({
    ...provided,
    backgroundColor: 'var(--bg-input, #2a2a2a)',
    borderColor: 'var(--border-color, #444)',
    color: 'var(--text-primary, #fff)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'var(--accent-primary, #007bff)'
    }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--bg-card, #1e1e1e)',
    border: '1px solid var(--border-color, #444)',
    zIndex: 10
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused 
      ? 'var(--accent-transparent, rgba(0, 123, 255, 0.1))'
      : 'transparent',
    color: 'var(--text-primary, #fff)',
    '&:active': {
      backgroundColor: 'var(--accent-primary, #007bff)',
    }
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: 'var(--accent-transparent, rgba(0, 123, 255, 0.2))',
    borderRadius: '4px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: 'var(--text-primary, #fff)',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: 'var(--text-secondary, #aaa)',
    ':hover': {
      backgroundColor: 'var(--accent-primary, #007bff)',
      color: 'white',
    },
  }),
  input: (provided) => ({
    ...provided,
    color: 'var(--text-primary, #fff)',
  }),
};

const extractPointCoordinates = (
  point?: { type?: string; coordinates?: [number, number] } | null,
  fallbackLongitude?: number | string,
  fallbackLatitude?: number | string
) => {
  if (point && Array.isArray(point.coordinates) && point.coordinates.length === 2) {
    return {
      longitude: String(point.coordinates[0]),
      latitude: String(point.coordinates[1])
    };
  }

  return {
    longitude: fallbackLongitude !== undefined && fallbackLongitude !== null ? String(fallbackLongitude) : '',
    latitude: fallbackLatitude !== undefined && fallbackLatitude !== null ? String(fallbackLatitude) : ''
  };
};

interface SortableMediaCardProps {
  mediaLink: any;
  onViewDetails: (id: string) => void;
}

const SortableMediaCard: React.FC<SortableMediaCardProps> = ({ mediaLink, onViewDetails }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mediaLink.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    position: 'relative' as const,
    zIndex: isDragging ? 1 : 0
  };

  const thumbnailUrl = mediaLink.posterUrl 
    ? `https://x-perienc.es/${mediaLink.posterUrl}`
    : null;

  return (
    <div ref={setNodeRef} style={style} className="media-card" {...attributes} {...listeners}>
      {thumbnailUrl ? (
        <div 
          className="media-preview"
          style={{ backgroundImage: `url(${thumbnailUrl})` }}
        >
          {mediaLink.type && (
            <div className="type-badge">
              {mediaLink.type === 1 && (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7"></polygon>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                </svg>
              )}
              {mediaLink.type === 2 && (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              )}
              {mediaLink.type === 3 && (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13"></path>
                  <circle cx="6" cy="18" r="3"></circle>
                  <circle cx="18" cy="16" r="3"></circle>
                </svg>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="media-preview" style={{ backgroundColor: '#333' }}>
          <span className="media-icon">📁</span>
          {mediaLink.type && (
            <div className="type-badge">
              {mediaLink.type === 1 && (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7"></polygon>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                </svg>
              )}
              {mediaLink.type === 2 && (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              )}
              {mediaLink.type === 3 && (
                <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13"></path>
                  <circle cx="6" cy="18" r="3"></circle>
                  <circle cx="18" cy="16" r="3"></circle>
                </svg>
              )}
            </div>
          )}
        </div>
      )}
      
      <div className="media-info" style={{ cursor: 'default' }} onPointerDown={(e) => e.stopPropagation()}>
        {mediaLink.title && (
          <div className="media-name">{mediaLink.title}</div>
        )}
        {mediaLink.description && (
          <div className="media-meta" style={{ marginTop: '4px' }}>
            {mediaLink.description.substring(0, 80)}
            {mediaLink.description.length > 80 ? '...' : ''}
          </div>
        )}
        <div className="media-meta">
          <span>{t('xperienceDetail.mediaCard.added')}: {mediaLink.createdDate ? new Date(mediaLink.createdDate).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : t('xperienceDetail.mediaCard.na')}</span>
        </div>
        <button
          className="media-delete"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails(mediaLink.id);
          }}
          style={{ backgroundColor: '#0066cc', color: 'white' }}
        >
          {t('xperienceDetail.mediaCard.viewDetails')}
        </button>
      </div>
    </div>
  );
};

const XperienceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Require moving 5px to start drag to avoid clicking issues
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && String(active.id) !== String(over.id) && xperience && xperience.mediaLinks) {
      const oldIndex = xperience.mediaLinks.findIndex(m => String(m.id) === String(active.id));
      const newIndex = xperience.mediaLinks.findIndex(m => String(m.id) === String(over.id));
      
      if (oldIndex === -1 || newIndex === -1) return;

      const newMediaLinks = arrayMove(xperience.mediaLinks, oldIndex, newIndex);
      
      // Optistically update UI
      setXperience({
        ...xperience,
        mediaLinks: newMediaLinks
      });

      // Prepare data for API (0-indexed order based on new positions)
      const itemsToUpdate = newMediaLinks.map((media, index) => ({
        id: String(media.id!),
        order: index
      }));

      try {
        await reorderXperienceMedia(id!, itemsToUpdate);
        setSuccess(t('xperienceDetail.success.mediaOrderSaved'));
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        console.error('Error reordering media:', err);
        setError(t('xperienceDetail.error.mediaOrderSave'));
        // Reload original data to revert UI on error
        loadXperience();
      }
    }
  };

  const [xperience, setXperience] = useState<Xperience | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Confirm dialogs
  const [showDeleteMediaConfirm, setShowDeleteMediaConfirm] = useState(false);
  const [selectedMediaIdToDelete, setSelectedMediaIdToDelete] = useState<string | null>(null);
  const [showDeleteXperienceConfirm, setShowDeleteXperienceConfirm] = useState(false);
  const [showCopyLocationAllConfirm, setShowCopyLocationAllConfirm] = useState(false);
  const [copyingLocationToAll, setCopyingLocationToAll] = useState(false);
  const [lastXperienceLocation, setLastXperienceLocation] = useState<{ longitude: string; latitude: string } | null>(null);

  const [emailCooldown, setEmailCooldown] = useState(0);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_XPERIENCE_LOCATION_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { longitude?: string; latitude?: string };
      if (!parsed.longitude || !parsed.latitude) return;
      setLastXperienceLocation({
        longitude: String(parsed.longitude),
        latitude: String(parsed.latitude)
      });
    } catch {
      // Ignore malformed localStorage value.
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    const cooldownKey = `qr_email_cooldown_${id}`;
    const savedCooldown = localStorage.getItem(cooldownKey);

    if (savedCooldown && emailCooldown === 0) {
      const expirationTime = parseInt(savedCooldown, 10);
      const remainingTime = Math.max(0, Math.floor((expirationTime - Date.now()) / 1000));
      if (remainingTime > 0) {
        setEmailCooldown(remainingTime);
      } else {
        localStorage.removeItem(cooldownKey);
      }
    }
  }, [id]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (emailCooldown > 0) {
      interval = setInterval(() => {
        setEmailCooldown((prev) => {
          if (prev <= 1) {
            if (id) localStorage.removeItem(`qr_email_cooldown_${id}`);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [emailCooldown, id]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isPublic: true,
    primaryLongitude: '',
    primaryLatitude: '',
  });
  const [selectedTags, setSelectedTags] = useState<MultiValue<TagOption>>([]);
  const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
  const [newSceneTitle, setNewSceneTitle] = useState('');
  const [editingSceneId, setEditingSceneId] = useState<string | null>(null);
  const [editingSceneTitle, setEditingSceneTitle] = useState('');
  const [sceneActionLoading, setSceneActionLoading] = useState(false);

  useEffect(() => {
    // Load available tags
    const loadTags = async () => {
      try {
        const response = await getXperienceTags();
        const formattedTags = response.data.tags.map((tag: any) => ({
          value: tag.id,
          label: tag.name
        }));
        setAvailableTags(formattedTags);
      } catch (err) {
        console.error('Failed to load available tags:', err);
      }
    };
    loadTags();
  }, []);

  useEffect(() => {
    if (id) {
      loadXperience();
    }
  }, [id, availableTags]);

  const loadXperience = async () => {
    try {
      const response = await getXperience(id!);
      const exp = response.data.xperience;
      const point = extractPointCoordinates(exp.primaryLocarion, exp.primaryLongitude, exp.primaryLatitude);
      setXperience(exp);
      setFormData({
        title: exp.title,
        description: exp.description || '',
        isPublic: exp.isPublic !== 0,
        primaryLongitude: point.longitude,
        primaryLatitude: point.latitude,
      });

      if (exp.tags && availableTags.length > 0) {
        const activeTags = exp.tags.map((t: any) => ({
          value: t.id,
          label: t.tag || t.name || availableTags.find(at => at.value === t.id)?.label || `Tag ${t.id}`
        }));
        setSelectedTags(activeTags);
      }
    } catch (err: any) {
      setError(t('xperienceDetail.error.load'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const tags = selectedTags.map(tag => tag.value);
      const apiData = {
        ...formData,
        isPublic: formData.isPublic ? 1 : 0,
        longitude: formData.primaryLongitude.trim() === '' ? null : Number(formData.primaryLongitude),
        latitude: formData.primaryLatitude.trim() === '' ? null : Number(formData.primaryLatitude),
        tags
      };
      await updateXperience(id!, apiData as any);

      if (formData.primaryLongitude.trim() !== '' && formData.primaryLatitude.trim() !== '') {
        const lastLocation = {
          longitude: formData.primaryLongitude,
          latitude: formData.primaryLatitude
        };
        localStorage.setItem(LAST_XPERIENCE_LOCATION_KEY, JSON.stringify(lastLocation));
        setLastXperienceLocation(lastLocation);
      }

      await loadXperience(); // Reload full xperience data including mediaLinks
      setEditing(false);
      setSuccess(`✓ ${t('xperienceDetail.success.updated')}`);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(t('xperienceDetail.error.update'));
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const handleCopyLastLocation = () => {
    if (!lastXperienceLocation) {
      setError(t('xperienceDetail.error.noLastLocation'));
      return;
    }

    setError('');
    setSuccess(t('xperienceDetail.success.lastLocationCopied'));
    setTimeout(() => setSuccess(''), 3000);
    setFormData((prev) => ({
      ...prev,
      primaryLongitude: lastXperienceLocation.longitude,
      primaryLatitude: lastXperienceLocation.latitude
    }));
  };

  const handleOpenCopyLocationToAllConfirm = () => {
    if (!xperience || !xperience.mediaLinks || xperience.mediaLinks.length === 0) {
      setError(t('xperienceDetail.error.noMediaToCopyLocationAll'));
      return;
    }

    if (formData.primaryLongitude.trim() === '' || formData.primaryLatitude.trim() === '') {
      setError(t('xperienceDetail.error.noLocationToCopyAll'));
      return;
    }

    setError('');
    setShowCopyLocationAllConfirm(true);
  };

  const handleConfirmCopyLocationToAll = async () => {
    if (!xperience || !xperience.mediaLinks || xperience.mediaLinks.length === 0) {
      setShowCopyLocationAllConfirm(false);
      setError(t('xperienceDetail.error.noMediaToCopyLocationAll'));
      return;
    }

    const longitude = Number(formData.primaryLongitude);
    const latitude = Number(formData.primaryLatitude);

    if (Number.isNaN(longitude) || Number.isNaN(latitude)) {
      setShowCopyLocationAllConfirm(false);
      setError(t('xperienceDetail.error.invalidLocationToCopyAll'));
      return;
    }

    const mediaIds = xperience.mediaLinks
      .map((link) => (link.id === undefined || link.id === null ? '' : String(link.id)))
      .filter((mediaId) => mediaId !== '');

    if (mediaIds.length === 0) {
      setShowCopyLocationAllConfirm(false);
      setError(t('xperienceDetail.error.noMediaToCopyLocationAll'));
      return;
    }

    setCopyingLocationToAll(true);
    setShowCopyLocationAllConfirm(false);
    setError('');
    setSuccess('');

    try {
      const payload = { longitude, latitude };
      const results = await Promise.allSettled(
        mediaIds.map((mediaId) => updateXperienceMedia(mediaId, payload))
      );

      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - successCount;

      if (successCount > 0 && failedCount === 0) {
        setSuccess(t('xperienceDetail.success.locationCopiedToAll').replace('{count}', String(successCount)));
      } else if (successCount > 0) {
        setError(
          t('xperienceDetail.error.locationCopiedToAllPartial')
            .replace('{success}', String(successCount))
            .replace('{failed}', String(failedCount))
        );
      } else {
        setError(t('xperienceDetail.error.locationCopiedToAllFailed'));
      }
    } catch {
      setError(t('xperienceDetail.error.locationCopiedToAllFailed'));
    } finally {
      setCopyingLocationToAll(false);
    }
  };

  const handleSendEmailQR = async () => {
    if (emailCooldown > 0) return;
    try {
      if (!id) return;
      setSuccess(t('xperienceDetail.sendingEmail'));
      await emailPrivateXperienceQR(id);
      setSuccess(t('xperienceDetail.success.emailSent'));
      
      const COOLDOWN_SECONDS = 180; // Desactivar 3 minutos
      setEmailCooldown(COOLDOWN_SECONDS);
      localStorage.setItem(`qr_email_cooldown_${id}`, (Date.now() + COOLDOWN_SECONDS * 1000).toString());

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(t('xperienceDetail.error.sendEmail'));
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleCreateXperienceMedia = () => {
    if (!id) return;
    navigate(`/xperience-media/new/${id}`);
  };

  const runSceneAction = async (action: () => Promise<void>, successMessage?: string) => {
    if (!id) return;
    setSceneActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await action();
      await loadXperience();
      if (successMessage) {
        setSuccess(successMessage);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || t('xperienceDetail.scenes.error.save'));
    } finally {
      setSceneActionLoading(false);
    }
  };

  const handleCreateScene = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = newSceneTitle.trim();
    if (!title || !id) return;

    await runSceneAction(async () => {
      await createXperienceScene(id, { title });
      setNewSceneTitle('');
    }, t('xperienceDetail.scenes.success.created'));
  };

  const handleStartEditScene = (scene: XperienceScene) => {
    setEditingSceneId(scene.id);
    setEditingSceneTitle(scene.title);
  };

  const handleSaveSceneTitle = async (sceneId: string) => {
    const title = editingSceneTitle.trim();
    if (!title || !id) return;

    await runSceneAction(async () => {
      await updateXperienceScene(id, sceneId, { title });
      setEditingSceneId(null);
      setEditingSceneTitle('');
    }, t('xperienceDetail.scenes.success.updated'));
  };

  const handleDeleteScene = async (sceneId: string) => {
    if (!id || !window.confirm(t('xperienceDetail.scenes.confirmDelete'))) return;

    await runSceneAction(async () => {
      await deleteXperienceScene(id, sceneId);
    }, t('xperienceDetail.scenes.success.deleted'));
  };

  const handleMoveScene = async (sceneId: string, direction: -1 | 1) => {
    if (!id || !xperience?.scenes) return;
    const currentIndex = xperience.scenes.findIndex((scene) => scene.id === sceneId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= xperience.scenes.length) return;

    const newScenes = arrayMove(xperience.scenes, currentIndex, nextIndex);
    setXperience({ ...xperience, scenes: newScenes });

    try {
      await reorderXperienceScenes(id, newScenes.map((scene, index) => ({ id: scene.id, order: index })));
    } catch {
      setError(t('xperienceDetail.scenes.error.reorder'));
      await loadXperience();
    }
  };

  const handleAddMediaToScene = async (sceneId: string, mediaId: string) => {
    if (!id || !mediaId) return;

    await runSceneAction(async () => {
      await addMediaToXperienceScene(id, sceneId, mediaId);
    }, t('xperienceDetail.scenes.success.mediaAdded'));
  };

  const handleRemoveMediaFromScene = async (sceneId: string, mediaId: string) => {
    if (!id) return;

    await runSceneAction(async () => {
      await removeMediaFromXperienceScene(id, sceneId, mediaId);
    }, t('xperienceDetail.scenes.success.mediaRemoved'));
  };

  const handleMoveSceneMedia = async (sceneId: string, mediaId: string, direction: -1 | 1) => {
    if (!id || !xperience?.scenes) return;
    const scene = xperience.scenes.find((item) => item.id === sceneId);
    const sceneMedia = scene?.media || [];
    const currentIndex = sceneMedia.findIndex((media) => String(media.id) === mediaId);
    const nextIndex = currentIndex + direction;
    if (!scene || currentIndex < 0 || nextIndex < 0 || nextIndex >= sceneMedia.length) return;

    const reorderedMedia = arrayMove(sceneMedia, currentIndex, nextIndex);
    const newScenes = xperience.scenes.map((item) => (
      item.id === sceneId ? { ...item, media: reorderedMedia } : item
    ));
    setXperience({ ...xperience, scenes: newScenes });

    try {
      await reorderXperienceSceneMedia(
        id,
        sceneId,
        reorderedMedia.map((media, index) => ({ mediaId: String(media.id), order: index }))
      );
    } catch {
      setError(t('xperienceDetail.scenes.error.reorderMedia'));
      await loadXperience();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      await uploadMedia(id!, file, (progress) => {
        setUploadProgress(progress);
      });
      await loadXperience(); // Reload to show new media
    } catch (err: any) {
      setError(t('xperienceDetail.error.uploadMedia'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteMedia = (mediaId: string) => {
    setSelectedMediaIdToDelete(mediaId);
    setShowDeleteMediaConfirm(true);
  };

  const confirmDeleteMedia = async () => {
    if (!selectedMediaIdToDelete) return;

    try {
      await deleteMedia(selectedMediaIdToDelete as any);
      await loadXperience();
      setError('');
    } catch (err: any) {
      setError(t('xperienceDetail.error.deleteMedia'));
    } finally {
      setShowDeleteMediaConfirm(false);
      setSelectedMediaIdToDelete(null);
    }
  };
  const handleDeleteXperience = () => {
    setShowDeleteXperienceConfirm(true);
  };

  const confirmDeleteXperience = async () => {
    try {
      await deleteXperience(id!);
      navigate('/');
    } catch (err: any) {
      setError(t('xperienceDetail.error.deleteXperience'));
    } finally {
      setShowDeleteXperienceConfirm(false);
    }
  };
  const getMediaUrl = (media: Media | undefined) => {
    if (!media) return '';
    return `https://api.x-perienc.es${media.filepath}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  if (loading) {
    return (
      <div className="detail-page">
        <Navbar />
        <div className="detail-loading">{t('xperienceDetail.loading')}</div>
      </div>
    );
  }

  if (!xperience) {
    return (
      <div className="detail-page">
        <Navbar />
        <div className="detail-error">{t('xperienceDetail.notFound')}</div>
      </div>
    );
  }

  const displayLocation = extractPointCoordinates(
    xperience.primaryLocarion,
    xperience.primaryLongitude,
    xperience.primaryLatitude
  );
  const hasDisplayLocation = displayLocation.longitude !== '' && displayLocation.latitude !== '';

  return (
    <div className="detail-page">
      <Navbar />
      
      <div className="detail-container">
        <button onClick={() => navigate('/')} className="back-button">
          ← {t('xperienceDetail.backDashboard')}
        </button>

        {error && <div className="detail-error">{error}</div>}
        {success && <div className="detail-success">{success}</div>}

        <div className="detail-card">
          {editing ? (
            <form onSubmit={handleUpdate} className="detail-form">
              <h2 className="detail-title">{t('xperienceDetail.editTitle')}</h2>
              
              <Input
                label={t('xperienceDetail.field.title')}
                name="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />

              <div className="input-wrapper">
                <label className="input-label">{t('xperienceDetail.field.description')}</label>
                <div className="clearable-field clearable-field-textarea">
                  <textarea
                    className="input clearable-textarea"
                    name="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                  />
                  {formData.description && (
                    <button
                      type="button"
                      className="clearable-field-button"
                      onClick={() => setFormData({ ...formData, description: '' })}
                      aria-label={t('common.clearSearch')}
                      title={t('common.clearSearch')}
                    >
                      x
                    </button>
                  )}
                </div>
              </div>

              <div className="input-wrapper">
                <label className="input-label">{t('xperienceDetail.field.location')}</label>
                <LocationPicker
                  longitude={formData.primaryLongitude}
                  latitude={formData.primaryLatitude}
                  onChange={({ longitude, latitude }) => {
                    setFormData((prev) => ({
                      ...prev,
                      primaryLongitude: longitude,
                      primaryLatitude: latitude
                    }));
                  }}
                  extraActions={
                    <>
                      <button
                        type="button"
                        className="location-picker-action-btn"
                        onClick={handleCopyLastLocation}
                        disabled={!lastXperienceLocation}
                      >
                        {t('xperienceDetail.copyLastLocation')}
                      </button>
                      <button
                        type="button"
                        className="location-picker-action-btn"
                        onClick={handleOpenCopyLocationToAllConfirm}
                        disabled={copyingLocationToAll}
                      >
                        {copyingLocationToAll ? t('xperienceDetail.copyLocationToAllLoading') : t('xperienceDetail.copyLocationToAll')}
                      </button>
                    </>
                  }
                />
              </div>

              <div className="input-wrapper">
                <label className="input-label">{t('xperienceDetail.field.tags')}</label>
                <Select
                  isMulti
                  name="tags"
                  options={availableTags}
                  value={selectedTags}
                  onChange={setSelectedTags}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder={t('xperienceDetail.placeholder.tags')}
                  styles={customSelectStyles}
                />
                <p className="input-help" style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  {t('xperienceDetail.help.tags')}
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={!formData.isPublic}
                  onChange={(e) => setFormData({ ...formData, isPublic: !e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="isPublic" style={{ cursor: 'pointer', color: 'var(--text-primary)', fontSize: '15px' }}>
                  {t('xperienceDetail.privateToggle')}
                </label>
              </div>

              <div className="detail-actions">
                <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                  {t('xperienceDetail.cancel')}
                </Button>
                <Button type="submit" variant="primary">
                  {t('xperienceDetail.saveChanges')}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <div className="detail-header">
                <h1 className="experience-title-large">{xperience.title}</h1>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button variant="ghost" onClick={() => setEditing(true)}>
                    {t('xperienceDetail.edit')}
                  </Button>
                  <button 
                    className="action-delete"
                    onClick={handleDeleteXperience}
                    style={{ padding: '8px 16px', fontSize: '14px' }}
                  >
                    {t('xperienceDetail.deleteXperience')}
                  </button>
                </div>
              </div>

              {xperience.description && (
                <p className="experience-description-large">{xperience.description}</p>
              )}

              <div className="experience-info">
                <div className="info-item">
                  <span className="info-label">{t('xperienceDetail.info.status')}</span>
                  <span className="info-value" style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: xperience.status === 'approved' ? 'rgba(76, 175, 80, 0.2)' : 
                                       xperience.status === 'rejected' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                      color: xperience.status === 'approved' ? '#4caf50' : 
                             xperience.status === 'rejected' ? '#f44336' : '#ff9800',
                      textTransform: 'capitalize',
                      fontWeight: 'bold'
                  }}>
                    {xperience.status || t('xperienceDetail.pending')}
                  </span>
                </div>
                <div className="info-item">
                    <span className="info-label">{t('xperienceDetail.info.views')}</span>
                    <span className="info-value">👁️ {xperience.views || 0}</span>
                </div>
                <div className="info-item">
                    <span className="info-label">{t('xperienceDetail.info.rating')}</span>
                    <span className="info-value">⭐ {xperience.rating ? Number(xperience.rating).toFixed(1) : '0.0'}</span>
                </div>
                <div className="info-item">
                    <span className="info-label">{t('xperienceDetail.info.reactions')}</span>
                    <span className="info-value">
                        <span style={{ marginRight: '10px' }}>👍 {xperience.likes_count || 0}</span>
                        <span>👎 {xperience.dislikes_count || 0}</span>
                    </span>
                </div>

                {xperience.tags && xperience.tags.length > 0 && (
                  <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="info-label" style={{ marginBottom: '8px', display: 'block' }}>{t('xperienceDetail.info.tags')}</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {xperience.tags.map(tag => (
                        <span key={tag.id} style={{ 
                          backgroundColor: 'var(--accent-transparent, rgba(0, 123, 255, 0.2))',
                          color: 'var(--text-primary, #fff)',
                          padding: '4px 10px',
                          borderRadius: '16px',
                          fontSize: '13px'
                        }}>
                          {tag.tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {xperience.category && (
                  <div className="info-item">
                    <span className="info-label">{t('xperienceDetail.info.category')}</span>
                    <span className="info-value">{xperience.category}</span>
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">{t('xperienceDetail.info.created')}</span>
                  <span className="info-value">
                    {xperience.createdDate 
                      ? new Date(xperience.createdDate).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) 
                      : t('xperienceDetail.noDate')}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t('xperienceDetail.info.mediaCount')}</span>
                  <span className="info-value">{xperience.mediaLinks?.length || 0}</span>
                </div>
                {hasDisplayLocation && (
                  <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="info-label">{t('xperienceDetail.field.location')}</span>
                    <span className="info-value">
                      {displayLocation.latitude}, {displayLocation.longitude}
                    </span>
                  </div>
                )}
                <div className="info-item">
                  <span className="info-label">{t('xperienceDetail.info.visibility')}</span>
                  <span className="info-value">
                    {xperience.isPublic === 0 ? t('xperienceDetail.visibility.private') : t('xperienceDetail.visibility.public')}
                  </span>
                </div>
              </div>

              {hasDisplayLocation && (
                <div style={{ marginTop: '24px' }}>
                  <LocationPicker
                    longitude={displayLocation.longitude}
                    latitude={displayLocation.latitude}
                    onChange={() => {}}
                    mapHeight={280}
                    readOnly
                  />
                </div>
              )}

              {xperience.isPublic === 0 && xperience.password && (
                <div style={{ marginTop: '24px', padding: '24px', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '12px', display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${xperience.password}`}
                    alt={t('xperienceDetail.privateQR.alt')}
                    style={{ borderRadius: '8px', background: 'white', padding: '8px', width: '120px', height: '120px' }}
                  />
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: 'var(--text-primary)' }}>{t('xperienceDetail.privateQR.title')}</h3>
                    <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      {t('xperienceDetail.privateQR.description')}
                    </p>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <span style={{ fontFamily: 'monospace', background: 'rgba(255,255,255,0.1)', padding: '6px 12px', borderRadius: '4px', color: 'var(--text-primary)', userSelect: 'all' }}>
                        {xperience.password}
                      </span>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(xperience.password || '');
                          setSuccess(t('xperienceDetail.success.passwordCopied'));
                          setTimeout(() => setSuccess(''), 3000);
                        }}
                        style={{ padding: '8px 16px', background: 'var(--accent-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
                      >
                        {t('xperienceDetail.copyPassword')}
                      </button>
                      <button 
                        onClick={handleSendEmailQR}
                        disabled={emailCooldown > 0}
                        style={{ 
                          padding: '8px 16px', 
                          background: 'var(--accent-secondary, rgba(255,255,255,0.1))', 
                          border: '1px solid rgba(255,255,255,0.2)', 
                          color: 'white', 
                          borderRadius: '6px', 
                          cursor: emailCooldown > 0 ? 'not-allowed' : 'pointer', 
                          fontSize: '14px', 
                          fontWeight: 500,
                          opacity: emailCooldown > 0 ? 0.5 : 1
                        }}
                      >
                        ✉️ {emailCooldown > 0 ? `${t('xperienceDetail.wait')} ${emailCooldown}s` : t('xperienceDetail.emailQR')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {!editing && (
          <div className="scenes-section">
            <div className="media-header">
              <div>
                <h2 className="section-title">{t('xperienceDetail.scenes.title')}</h2>
                <p className="section-subtitle">{t('xperienceDetail.scenes.subtitle')}</p>
              </div>
            </div>

            <form className="scene-create-row" onSubmit={handleCreateScene}>
              <Input
                label={t('xperienceDetail.scenes.newLabel')}
                name="sceneTitle"
                value={newSceneTitle}
                onChange={(e) => setNewSceneTitle(e.target.value)}
                placeholder={t('xperienceDetail.scenes.newPlaceholder')}
              />
              <Button type="submit" variant="primary" disabled={sceneActionLoading || !newSceneTitle.trim()}>
                {t('xperienceDetail.scenes.create')}
              </Button>
            </form>

            {xperience.scenes && xperience.scenes.length > 0 ? (
              <div className="scenes-list">
                {xperience.scenes.map((scene, sceneIndex) => {
                  const sceneMedia = scene.media || [];
                  const sceneMediaIds = new Set(sceneMedia.map((media) => String(media.id)));
                  const availableMedia = (xperience.mediaLinks || []).filter((media) => media.id && !sceneMediaIds.has(String(media.id)));
                  const selectId = `scene-media-${scene.id}`;

                  return (
                    <div className="scene-card" key={scene.id}>
                      <div className="scene-card-header">
                        <div className="scene-title-block">
                          <span className="scene-index">{String(sceneIndex + 1).padStart(2, '0')}</span>
                          {editingSceneId === scene.id ? (
                            <input
                              className="scene-title-input"
                              value={editingSceneTitle}
                              onChange={(e) => setEditingSceneTitle(e.target.value)}
                              autoFocus
                            />
                          ) : (
                            <h3>{scene.title}</h3>
                          )}
                        </div>
                        <div className="scene-actions">
                          <button
                            type="button"
                            className="scene-action-btn"
                            onClick={() => handleMoveScene(scene.id, -1)}
                            disabled={sceneIndex === 0 || sceneActionLoading}
                          >
                            {t('xperienceDetail.scenes.moveUp')}
                          </button>
                          <button
                            type="button"
                            className="scene-action-btn"
                            onClick={() => handleMoveScene(scene.id, 1)}
                            disabled={sceneIndex === xperience.scenes!.length - 1 || sceneActionLoading}
                          >
                            {t('xperienceDetail.scenes.moveDown')}
                          </button>
                          {editingSceneId === scene.id ? (
                            <>
                              <button
                                type="button"
                                className="scene-action-btn scene-action-primary"
                                onClick={() => handleSaveSceneTitle(scene.id)}
                                disabled={sceneActionLoading || !editingSceneTitle.trim()}
                              >
                                {t('xperienceDetail.scenes.save')}
                              </button>
                              <button
                                type="button"
                                className="scene-action-btn"
                                onClick={() => setEditingSceneId(null)}
                              >
                                {t('xperienceDetail.cancel')}
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="scene-action-btn"
                              onClick={() => handleStartEditScene(scene)}
                            >
                              {t('xperienceDetail.edit')}
                            </button>
                          )}
                          <button
                            type="button"
                            className="scene-action-btn scene-action-danger"
                            onClick={() => handleDeleteScene(scene.id)}
                            disabled={sceneActionLoading}
                          >
                            {t('xperienceDetail.confirm.delete')}
                          </button>
                        </div>
                      </div>

                      <div className="scene-add-media">
                        <select
                          id={selectId}
                          className="scene-media-select"
                          defaultValue=""
                          disabled={availableMedia.length === 0 || sceneActionLoading}
                        >
                          <option value="">{availableMedia.length > 0 ? t('xperienceDetail.scenes.selectMedia') : t('xperienceDetail.scenes.noAvailableMedia')}</option>
                          {availableMedia.map((media) => (
                            <option key={media.id} value={media.id}>
                              {media.title || media.id}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="scene-action-btn scene-action-primary"
                          disabled={availableMedia.length === 0 || sceneActionLoading}
                          onClick={() => {
                            const select = document.getElementById(selectId) as HTMLSelectElement | null;
                            if (!select?.value) return;
                            handleAddMediaToScene(scene.id, select.value);
                            select.value = '';
                          }}
                        >
                          {t('xperienceDetail.scenes.addMedia')}
                        </button>
                      </div>

                      {sceneMedia.length > 0 ? (
                        <div className="scene-media-list">
                          {sceneMedia.map((media, mediaIndex) => (
                            <div className="scene-media-row" key={media.id}>
                              <div className="scene-media-summary">
                                {media.posterUrl ? (
                                  <img src={`https://x-perienc.es/${media.posterUrl}`} alt="" />
                                ) : (
                                  <span className="scene-media-placeholder">-</span>
                                )}
                                <div>
                                  <strong>{media.title || t('xperienceDetail.scenes.untitledMedia')}</strong>
                                  {media.description && <span>{media.description}</span>}
                                </div>
                              </div>
                              <div className="scene-media-actions">
                                <button
                                  type="button"
                                  className="scene-action-btn"
                                  onClick={() => handleMoveSceneMedia(scene.id, String(media.id), -1)}
                                  disabled={mediaIndex === 0 || sceneActionLoading}
                                >
                                  {t('xperienceDetail.scenes.moveUp')}
                                </button>
                                <button
                                  type="button"
                                  className="scene-action-btn"
                                  onClick={() => handleMoveSceneMedia(scene.id, String(media.id), 1)}
                                  disabled={mediaIndex === sceneMedia.length - 1 || sceneActionLoading}
                                >
                                  {t('xperienceDetail.scenes.moveDown')}
                                </button>
                                <button
                                  type="button"
                                  className="scene-action-btn scene-action-danger"
                                  onClick={() => handleRemoveMediaFromScene(scene.id, String(media.id))}
                                  disabled={sceneActionLoading}
                                >
                                  {t('xperienceDetail.scenes.removeMedia')}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="scene-empty">{t('xperienceDetail.scenes.emptyMedia')}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="media-empty">
                <span className="empty-icon">□</span>
                <p>{t('xperienceDetail.scenes.empty')}</p>
              </div>
            )}
          </div>
        )}

        {!editing && (
          <div className="media-section">
            <div className="media-header">
              <h2 className="section-title">{t('xperienceDetail.mediaSectionTitle')}</h2>
              <Button variant="primary" onClick={handleCreateXperienceMedia}>
                {t('xperienceDetail.createMedia')}
              </Button>
              {/* Keep file input available for direct uploads if needed */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,audio/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>

            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            )}

            {xperience.mediaLinks && xperience.mediaLinks.length > 0 ? (
              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext 
                  items={xperience.mediaLinks.map(m => String(m.id!))}
                  strategy={rectSortingStrategy}
                >
                  <div className="media-grid">
                    {xperience.mediaLinks.map((mediaLink) => (
                      <SortableMediaCard
                        key={mediaLink.id}
                        mediaLink={{...mediaLink, id: String(mediaLink.id)}}
                        onViewDetails={(id) => navigate(`/xperience-media/${id}`)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="media-empty">
                <span className="empty-icon">📁</span>
                <p>{t('xperienceDetail.noMedia')}</p>
              </div>
            )}
          </div>
        )}

      <ConfirmDialog
        isOpen={showDeleteMediaConfirm}
        title={t('xperienceDetail.confirm.deleteMediaTitle')}
        message={t('xperienceDetail.confirm.deleteMediaMessage')}
        confirmText={t('xperienceDetail.confirm.delete')}
        cancelText={t('xperienceDetail.confirm.cancel')}
        isDangerous={true}
        onConfirm={confirmDeleteMedia}
        onCancel={() => {
          setShowDeleteMediaConfirm(false);
          setSelectedMediaIdToDelete(null);
        }}
      />

      <ConfirmDialog
        isOpen={showDeleteXperienceConfirm}
        title={t('xperienceDetail.confirm.deleteXperienceTitle')}
        message={t('xperienceDetail.confirm.deleteXperienceMessage')}
        confirmText={t('xperienceDetail.confirm.delete')}
        cancelText={t('xperienceDetail.confirm.cancel')}
        isDangerous={true}
        onConfirm={confirmDeleteXperience}
        onCancel={() => setShowDeleteXperienceConfirm(false)}
      />

      <ConfirmDialog
        isOpen={showCopyLocationAllConfirm}
        title={t('xperienceDetail.confirm.copyLocationAllTitle')}
        message={t('xperienceDetail.confirm.copyLocationAllMessage')}
        confirmText={t('xperienceDetail.confirm.copyLocationAllConfirm')}
        cancelText={t('xperienceDetail.confirm.copyLocationAllCancel')}
        isDangerous={true}
        onConfirm={handleConfirmCopyLocationToAll}
        onCancel={() => setShowCopyLocationAllConfirm(false)}
      />
      </div>
    </div>
  );
};

export default XperienceDetailPage;
